import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import { useDemoContext } from '../../contexts/DemoContext';

const emptyForm = {
  name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  status: 'active',
};

export default function DemoVendorsPage() {
  const {
    vendors,
    policies,
    stats,
    createVendor,
    updateVendor,
    deleteVendor,
  } = useDemoContext();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const sortedVendors = useMemo(() => (
    [...vendors].sort((left, right) => {
      if (left.status !== right.status) {
        return left.status === 'active' ? -1 : 1;
      }
      return left.name.localeCompare(right.name);
    })
  ), [vendors]);

  const policyCounts = useMemo(() => (
    policies.reduce((accumulator, policy) => {
      accumulator[policy.vendor_id] = (accumulator[policy.vendor_id] || 0) + 1;
      return accumulator;
    }, {})
  ), [policies]);

  const handleToggleArchive = (vendor) => {
    updateVendor(vendor.id, {
      status: vendor.status === 'active' ? 'archived' : 'active',
    });
  };

  const handleDelete = (vendor) => {
    if (!window.confirm(`Delete ${vendor.name} and remove its demo policies?`)) {
      return;
    }

    deleteVendor(vendor.id);
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="text-gray-600 mt-1">
            {stats.active_vendors} / {stats.vendor_limit} active vendors
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500 bg-yellow-50 border border-yellow-200 px-4 py-2 rounded-lg">
            Demo Mode: Changes won't be saved
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vendor
          </button>
        </div>
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">All Vendors</h2>
          <p className="text-sm text-gray-500">
            Create vendors here, then use the Policies page to add coverage.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Policies
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedVendors.map((vendor) => (
                <tr key={vendor.id} className={vendor.status === 'archived' ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{vendor.name}</div>
                      {vendor.is_self && (
                        <span className="text-xs text-gray-500">(Your Company)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="text-gray-900">{vendor.contact_name || 'No contact name'}</div>
                      <div className="text-gray-500">{vendor.contact_email || 'No email'}</div>
                      <div className="text-gray-500">{vendor.contact_phone || 'No phone'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      vendor.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {vendor.status === 'active' ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <Link
                      to={`/demo/policies?vendor=${vendor.id}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {policyCounts[vendor.id] || 0} polic{policyCounts[vendor.id] === 1 ? 'y' : 'ies'}
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingVendor(vendor)}
                        className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleArchive(vendor)}
                        className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        {vendor.status === 'active' ? (
                          <>
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </>
                        ) : (
                          <>
                            <ArchiveRestore className="h-4 w-4 mr-2" />
                            Restore
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(vendor)}
                        className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>In Demo Mode:</strong> Start by creating a few subcontractors here, then jump into their policies to see how expiration risk rolls up across the rest of the app.
        </p>
      </div>

      {(showCreateModal || editingVendor) && (
        <DemoVendorModal
          vendor={editingVendor}
          onClose={() => {
            setShowCreateModal(false);
            setEditingVendor(null);
          }}
          onSave={(formData) => {
            if (editingVendor) {
              updateVendor(editingVendor.id, formData);
            } else {
              createVendor(formData);
            }
            setShowCreateModal(false);
            setEditingVendor(null);
          }}
        />
      )}
    </div>
  );
}

function DemoVendorModal({ vendor, onClose, onSave }) {
  const [formData, setFormData] = useState(() => ({
    ...emptyForm,
    ...(vendor || {}),
  }));
  const [error, setError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      setError('Vendor name is required');
      return;
    }

    setError('');
    onSave({
      name: formData.name.trim(),
      contact_name: formData.contact_name.trim(),
      contact_email: formData.contact_email.trim(),
      contact_phone: formData.contact_phone.trim(),
      address: formData.address.trim(),
      status: formData.status,
      is_self: Boolean(vendor?.is_self),
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black opacity-30" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h2 className="text-xl font-semibold mb-6">
            {vendor ? 'Edit Vendor' : 'Add Vendor'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Company Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(event) => setFormData((previous) => ({ ...previous, name: event.target.value }))}
                className="input"
                placeholder="ABC Electrical Services"
                required
              />
            </div>

            <div>
              <label className="label">Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(event) => setFormData((previous) => ({ ...previous, contact_name: event.target.value }))}
                className="input"
                placeholder="John Smith"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(event) => setFormData((previous) => ({ ...previous, contact_email: event.target.value }))}
                  className="input"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="label">Phone</label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(event) => setFormData((previous) => ({ ...previous, contact_phone: event.target.value }))}
                  className="input"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div>
              <label className="label">Address</label>
              <textarea
                value={formData.address}
                onChange={(event) => setFormData((previous) => ({ ...previous, address: event.target.value }))}
                className="input"
                rows={2}
                placeholder="123 Main St, City, State 12345"
              />
            </div>

            {vendor && (
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.status}
                  onChange={(event) => setFormData((previous) => ({ ...previous, status: event.target.value }))}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {vendor ? 'Save Changes' : 'Add Vendor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
